import { inject, Injectable, NgZone, signal } from '@angular/core';
import { AuthService } from './auth.service';
import { LOCK_AT_KEY, LOCK_FLAG_KEY, purgerVerrou } from './session-storage-keys';

/** Inactivite avant verrouillage automatique de la session. */
const IDLE_LIMIT_MS = 15 * 60 * 1000;
/** Duree max de verrouillage avant deconnexion sèche (alignee sur le refresh token 12h). */
const MAX_LOCK_MS = 12 * 60 * 60 * 1000;
/** Fenetre d'avertissement avant verrouillage : l'ecran ne se floute plus sans preavis. */
const AVERTISSEMENT_MS = 60 * 1000;
/** Frequence de verification du minuteur. A la seconde, pour que le compte a rebours soit lisible. */
const TICK_MS = 1000;

/**
 * Evenements comptes comme activite reelle.
 *
 * `mousemove` et `wheel` en sont **volontairement absents**. Les y inclure faisait rearmer
 * quinze minutes au moindre deplacement du curseur : une souris posee sur un bureau qui vibre,
 * un ecran tactile effleure ou une extension de navigateur suffisaient a ce que le verrou ne
 * tombe jamais. Ne restent que les gestes qui traduisent une intention.
 */
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ['mousedown', 'keydown', 'scroll', 'touchstart'];

/**
 * Gestion du verrouillage de session sur inactivite.
 *
 * - 14 min sans activite → avertissement avec compte a rebours de 60 s.
 * - 15 min sans activite → etat « verrouille » (overlay flou + ré-auth, cf. LockOverlayComponent).
 * - L'activite reelle (clic, clavier, scroll, toucher) rearme le minuteur.
 * - Le verrou persiste au rechargement **et dans un onglet neuf** (localStorage, partage par
 *   origine) et se propage aux onglets ouverts via BroadcastChannel.
 * - Au-dela de 12h de verrouillage → deconnexion sèche.
 *
 * Demarre/arrete par le LayoutComponent (zone authentifiee uniquement).
 */
@Injectable({ providedIn: 'root' })
export class IdleSessionService {
    private readonly authService = inject(AuthService);
    private readonly zone = inject(NgZone);

    private readonly _locked = signal(this.readLockFlag());
    readonly locked = this._locked.asReadonly();

    /** Secondes avant verrouillage, non nul dans la seule fenetre d'avertissement. */
    private readonly _secondesRestantes = signal(0);
    readonly secondesRestantes = this._secondesRestantes.asReadonly();

    private lastActivity = Date.now();
    private lastBroadcast = 0;
    private intervalId: ReturnType<typeof setInterval> | null = null;
    private channel: BroadcastChannel | null = null;
    private started = false;
    private readonly onActivity = (): void => this.registerActivity();

    start(): void {
        if (this.started) {
            return;
        }
        this.started = true;
        this.lastActivity = Date.now();

        this.zone.runOutsideAngular(() => {
            ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, this.onActivity, { passive: true }));
            this.intervalId = setInterval(() => this.tick(), TICK_MS);
        });

        if (typeof BroadcastChannel !== 'undefined') {
            this.channel = new BroadcastChannel('sispe-session');
            this.channel.onmessage = (event: MessageEvent<string>): void => this.onChannelMessage(event.data);
        }
    }

    stop(): void {
        if (!this.started) {
            return;
        }
        this.started = false;
        ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, this.onActivity));
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.channel?.close();
        this.channel = null;
    }

    /** Verrouille immediatement (utilisable pour un futur bouton « Verrouiller »). */
    lock(): void {
        this.applyLock(true);
        this.channel?.postMessage('lock');
    }

    /** Deverrouille apres ré-auth reussie. */
    unlock(): void {
        this._locked.set(false);
        this._secondesRestantes.set(0);
        this.lastActivity = Date.now();
        purgerVerrou();
        this.channel?.postMessage('unlock');
    }

    /** Rearme le minuteur depuis l'avertissement, sans ressaisie de mot de passe. */
    resterConnecte(): void {
        this.lastActivity = Date.now();
        this._secondesRestantes.set(0);
        this.channel?.postMessage('activity');
    }

    /** Deconnexion sèche : purge le verrou puis termine la session. */
    forceLogout(): void {
        this.unlock();
        this.authService.logout();
    }

    private registerActivity(): void {
        if (this._locked()) {
            return;
        }
        this.lastActivity = Date.now();
        // Sortir de l'avertissement sans attendre le tick suivant. La condition evite
        // d'entrer dans la zone Angular a chaque scroll.
        if (this._secondesRestantes() !== 0) {
            this.zone.run(() => this._secondesRestantes.set(0));
        }
        // Throttle de la diffusion inter-onglets a 1s.
        if (this.lastActivity - this.lastBroadcast > 1000) {
            this.lastBroadcast = this.lastActivity;
            this.channel?.postMessage('activity');
        }
    }

    private tick(): void {
        if (this._locked()) {
            this.tickVerrouille();

            return;
        }
        const restant = IDLE_LIMIT_MS - (Date.now() - this.lastActivity);
        if (restant <= 0) {
            this.zone.run(() => this.lock());

            return;
        }
        this.majAvertissement(restant);
    }

    /**
     * Au-dela de la duree max de verrouillage, la session se termine.
     *
     * Un horodatage absent, illisible ou situe dans le futur ne peut venir que d'une alteration
     * du stockage : il termine la session au lieu de la prolonger sans fin (audit A-07).
     */
    private tickVerrouille(): void {
        const brut = localStorage.getItem(LOCK_AT_KEY);
        const ecoule = Date.now() - Number(brut);
        if (!brut || !Number.isFinite(ecoule) || ecoule < 0 || ecoule > MAX_LOCK_MS) {
            this.zone.run(() => this.forceLogout());
        }
    }

    /** N'entre dans la zone Angular que si la valeur affichee change reellement. */
    private majAvertissement(restantMs: number): void {
        const cible = restantMs <= AVERTISSEMENT_MS ? Math.ceil(restantMs / 1000) : 0;
        if (cible !== this._secondesRestantes()) {
            this.zone.run(() => this._secondesRestantes.set(cible));
        }
    }

    private onChannelMessage(data: string): void {
        switch (data) {
            case 'activity':
                this.lastActivity = Date.now();
                break;
            case 'lock':
                this.zone.run(() => this.applyLock(false));
                break;
            case 'unlock':
                this.zone.run(() => {
                    this._locked.set(false);
                    this._secondesRestantes.set(0);
                    this.lastActivity = Date.now();
                    purgerVerrou();
                });
                break;
        }
    }

    private applyLock(persistTimestamp: boolean): void {
        this._locked.set(true);
        this._secondesRestantes.set(0);
        localStorage.setItem(LOCK_FLAG_KEY, '1');
        if (persistTimestamp) {
            localStorage.setItem(LOCK_AT_KEY, String(Date.now()));
        }
    }

    private readLockFlag(): boolean {
        return localStorage.getItem(LOCK_FLAG_KEY) === '1';
    }
}
